import { MAX_VOICE_BYTES, MAX_VOICE_TRACKS_PER_DEMO, nudgeVoice, partAt, partStarts, syncVoiceAt, type DemoDetail, type Replay, type VoiceTrack } from "@69ly/shared";
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Icon } from "../components/Icon.tsx";
import type { RoundIndex } from "../demo/scene.ts";
import { audible, audioClock, parseAudioClock, VOICE_MAX_SPEED, type VoiceMix, type VoicePlayback } from "../demo/voice.ts";
import { useQueryClient } from "@tanstack/react-query";
import { matchKeys, uploadVoice, useDeleteVoice, useUpdateVoice, voiceAudioUrl } from "../lib/demo-queries.ts";
import { useAddVoiceSession, useMapVoiceSessions } from "../lib/discord-queries.ts";
import { errorText } from "../lib/errors.ts";

const AUDIO_ACCEPT = "audio/*,.mp3,.ogg,.opus,.oga,.m4a,.webm,.wav,.flac";

/**
 * The voice tracks of a map: who is heard, how loud, alone or not; and a file to add.
 * The tracks play wherever the viewer is; this panel only mixes them.
 */
export function VoiceTracksPanel({
  demo,
  tracks,
  mix,
  onMix,
  playback,
  selected,
  onSelect,
  speed,
}: {
  demo: DemoDetail;
  tracks: VoiceTrack[];
  mix: VoiceMix;
  onMix: (change: Partial<VoiceMix> | ((m: VoiceMix) => Partial<VoiceMix>)) => void;
  playback: VoicePlayback;
  selected: string | null;
  onSelect: (id: string) => void;
  speed: number;
}) {
  const { t } = useTranslation();
  return (
    <div className="voice-panel">
      {tracks.length === 0 ? (
        <div className="voice-empty">
          <span className="voice-empty-icon" aria-hidden="true">
            <Icon name="mic" size={22} />
          </span>
          <h3>{t("demos.voice.emptyTitle")}</h3>
          <p className="muted small">{t("demos.voice.emptyHelp")}</p>
        </div>
      ) : (
        <>
          <label className="switch-row voice-master">
            <span>
              <b>{t("demos.voice.on")}</b>
              <span className="muted small">{speed > VOICE_MAX_SPEED ? t("demos.voice.tooFast", { max: VOICE_MAX_SPEED }) : t("demos.voice.onHelp")}</span>
            </span>
            <input type="checkbox" role="switch" className="switch" checked={mix.on} onChange={(e) => onMix({ on: e.target.checked })} />
          </label>
          <ul className="voice-tracks" aria-label={t("demos.voice.tracks")}>
            {tracks.map((track) => {
              const at = playback.at(track);
              const length = playback.durations[track.id];
              const where = at < 0 ? t("demos.voice.notYet", { in: audioClock(-at) }) : length !== undefined && at >= length ? t("demos.voice.ended") : audioClock(at);
              const heard = audible(mix, track.id);
              return (
                <li key={track.id} className={`voice-track${selected === track.id ? " is-selected" : ""}${heard ? "" : " is-silent"}`}>
                  <button type="button" className="voice-track-name" aria-pressed={selected === track.id} onClick={() => onSelect(track.id)} title={t("demos.voice.lineUpThis")}>
                    <b>{track.label}</b>
                    <span className="muted small mono">{where}</span>
                  </button>
                  <div className="voice-track-mix">
                    <button
                      type="button"
                      className="btn btn-quiet btn-icon btn-small"
                      aria-pressed={!!mix.muted[track.id]}
                      aria-label={t(mix.muted[track.id] ? "demos.voice.unmute" : "demos.voice.mute", { name: track.label })}
                      title={t(mix.muted[track.id] ? "demos.voice.unmute" : "demos.voice.mute", { name: track.label })}
                      onClick={() => onMix((m) => ({ muted: { ...m.muted, [track.id]: !m.muted[track.id] } }))}
                    >
                      <Icon name={mix.muted[track.id] ? "muted" : "volume"} />
                    </button>
                    <input
                      type="range"
                      className="voice-volume"
                      min={0}
                      max={1}
                      step={0.05}
                      value={mix.volume[track.id] ?? 1}
                      aria-label={t("demos.voice.volume", { name: track.label })}
                      onChange={(e) => onMix((m) => ({ volume: { ...m.volume, [track.id]: Number(e.target.value) } }))}
                    />
                    <button
                      type="button"
                      className="btn btn-quiet btn-icon btn-small"
                      aria-pressed={mix.solo === track.id}
                      aria-label={t("demos.voice.solo", { name: track.label })}
                      title={t("demos.voice.solo", { name: track.label })}
                      onClick={() => onMix((m) => ({ solo: m.solo === track.id ? null : track.id }))}
                    >
                      <Icon name="headphones" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
      {demo.canAddVoice && tracks.length < MAX_VOICE_TRACKS_PER_DEMO && <FromDiscord demoId={demo.id} empty={tracks.length === 0} />}
      {demo.canAddVoice && tracks.length < MAX_VOICE_TRACKS_PER_DEMO && <AddVoice demoId={demo.id} onAdded={onSelect} first={tracks.length === 0} />}
      {tracks.length === 0 && !demo.canAddVoice && <p className="muted small">{t("demos.voice.cannotAdd")}</p>}
    </div>
  );
}

/** The team's Discord recordings made around this match: each added in one click, one track per player. */
function FromDiscord({ demoId, empty }: { demoId: string; empty: boolean }) {
  const { t, i18n } = useTranslation();
  const sessions = useMapVoiceSessions(demoId, true);
  const add = useAddVoiceSession(demoId);
  const list = sessions.data ?? [];
  if (!list.length) {
    return empty && sessions.isSuccess ? (
      <p className="muted small voice-discord-hint">
        {t("demos.voice.discordHint")} <Link to="/team#discord">{t("demos.voice.discordSetUp")}</Link>
      </p>
    ) : null;
  }
  return (
    <section className="voice-discord" aria-labelledby={`discord-${demoId}`}>
      <h3 id={`discord-${demoId}`} className="eyebrow">
        {t("demos.voice.fromDiscord")}
      </h3>
      <ul>
        {list.map((s) => {
          const all = s.added >= s.tracks.length;
          return (
            <li key={s.id}>
              <span>
                <b>{s.channelName}</b>
                <span className="muted small">
                  {new Date(s.startedAt).toLocaleString(i18n.language, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} ·{" "}
                  {s.tracks.map((x) => x.name).join(", ")}
                </span>
              </span>
              <button type="button" className="btn btn-secondary btn-small" disabled={all || add.isPending} onClick={() => add.mutate(s.id)}>
                {all ? t("demos.voice.added") : t("demos.voice.addRecording", { count: s.tracks.length - s.added })}
              </button>
            </li>
          );
        })}
      </ul>
      {add.error && <p className="field-error">{errorText(t, add.error)}</p>}
      {add.isSuccess && <p className="muted small">{t("demos.voice.addedHelp")}</p>}
    </section>
  );
}

/** A file picked, named, sent with its progress. */
function AddVoice({ demoId, onAdded, first }: { demoId: string; onAdded: (id: string) => void; first: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [label, setLabel] = useState("");
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const labelId = useId();

  const pick = (f: File | undefined) => {
    setError(null);
    if (!f) return;
    if (f.size > MAX_VOICE_BYTES) return setError(t("demos.voice.tooBig", { mb: MAX_VOICE_BYTES / 1024 / 1024 }));
    setFile(f);
    setLabel(f.name.replace(/\.[^.]+$/, "").slice(0, 60));
  };
  const send = async (e: FormEvent) => {
    e.preventDefault();
    if (!file) return;
    setError(null);
    setProgress(0);
    try {
      const track = await uploadVoice(demoId, file, label.trim() || file.name, setProgress);
      await qc.invalidateQueries({ queryKey: matchKeys.voices(demoId) });
      setFile(null);
      if (input.current) input.current.value = "";
      onAdded(track.id);
    } catch (err) {
      setError(errorText(t, err));
    } finally {
      setProgress(null);
    }
  };

  return (
    <form className="voice-add" onSubmit={(e) => void send(e)}>
      <input ref={input} type="file" accept={AUDIO_ACCEPT} className="visually-hidden" tabIndex={-1} aria-hidden="true" onChange={(e) => pick(e.target.files?.[0])} />
      {!file ? (
        <button type="button" className={`btn ${first ? "btn-primary" : "btn-secondary"} btn-small`} onClick={() => input.current?.click()}>
          <Icon name="upload" />
          {t("demos.voice.add")}
        </button>
      ) : (
        <>
          <p className="small voice-file">
            <Icon name="mic" />
            <span>{file.name}</span>
            <span className="muted mono">{(file.size / 1e6).toFixed(1)} MB</span>
          </p>
          <label className="field" htmlFor={labelId}>
            <span>{t("demos.voice.label")}</span>
            <input id={labelId} value={label} maxLength={60} placeholder={t("demos.voice.labelPlaceholder")} onChange={(e) => setLabel(e.target.value)} disabled={progress !== null} />
          </label>
          {progress !== null && <progress className="clip-progress" value={progress} max={1} aria-label={t("demos.voice.sending", { percent: Math.round(progress * 100) })} />}
          <div className="row">
            <button type="submit" className="btn btn-primary btn-small" disabled={progress !== null}>
              {progress !== null ? t("demos.voice.sending", { percent: Math.round(progress * 100) }) : t("demos.voice.send")}
            </button>
            <button type="button" className="btn btn-quiet btn-small" disabled={progress !== null} onClick={() => setFile(null)}>
              {t("common.cancel")}
            </button>
          </div>
        </>
      )}
      {first && !file && <p className="muted small">{t("demos.voice.formats", { mb: MAX_VOICE_BYTES / 1024 / 1024 })}</p>}
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}

const NUDGES = [-10, -1, -0.1, 0.1, 1, 10] as const;

/**
 * Lining a track up with the replay: nudges, a time typed in, or the moment
 * found by ear in the audio itself, matched to the moment on screen.
 */
export function VoiceSyncPanel({
  demo,
  replay,
  round,
  tick,
  track,
  playback,
}: {
  demo: DemoDetail;
  replay: Replay;
  round: RoundIndex;
  tick: number;
  track: VoiceTrack | null;
  playback: VoicePlayback;
}) {
  const { t } = useTranslation();
  if (!track) return <p className="muted small voice-sync-none">{t("demos.voice.pickOne")}</p>;
  return <VoiceSync key={track.id} demo={demo} replay={replay} round={round} tick={tick} track={track} playback={playback} />;
}

function VoiceSync({ demo, replay, round, tick, track, playback }: { demo: DemoDetail; replay: Replay; round: RoundIndex; tick: number; track: VoiceTrack; playback: VoicePlayback }) {
  const { t } = useTranslation();
  const update = useUpdateVoice(demo.id);
  const del = useDeleteVoice(demo.id);
  const preview = useRef<HTMLAudioElement>(null);
  const [heardAt, setHeardAt] = useState(0);
  const [typed, setTyped] = useState("");
  const [name, setName] = useState(track.label);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const starts = partStarts(replay);
  const part = partAt(replay, tick).part;
  const at = playback.at(track);
  const roundSeconds = (tick - round.round.startTick) / replay.tickRate;
  const here = t("demos.notes.at", { n: round.round.number, clock: `${Math.floor(roundSeconds / 60)}:${String(Math.floor(roundSeconds % 60)).padStart(2, "0")}` });
  const fail = (e: unknown) => setError(errorText(t, e));
  const save = (offsets: number[]) => {
    setError(null);
    update.mutate({ id: track.id, offsets }, { onError: fail });
  };

  // The ear-finding player starts where the voice is now.
  useEffect(() => {
    const el = preview.current;
    if (el && el.paused && at >= 0) el.currentTime = at;
    // Only when the replay moves, not while listening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  const typedSeconds = parseAudioClock(typed);

  return (
    <div className="voice-sync">
      <div className="voice-readout" aria-live="polite">
        <span className="eyebrow">{here}</span>
        <b className="mono">{at < 0 ? t("demos.voice.beforeStart") : audioClock(at)}</b>
        <span className="muted small">{t("demos.voice.readout", { name: track.label })}</span>
      </div>
      {starts.length > 1 && <p className="muted small">{t("demos.voice.parts", { n: part + 1, of: starts.length })}</p>}

      {!track.canEdit ? (
        <p className="muted small">{t("demos.voice.readOnly")}</p>
      ) : (
        <>
          <fieldset className="voice-nudge">
            <legend className="eyebrow">{t("demos.voice.nudge")}</legend>
            <div className="voice-nudge-row">
              {NUDGES.map((s) => (
                <button
                  key={s}
                  type="button"
                  className="btn btn-secondary btn-small mono"
                  aria-label={t(s < 0 ? "demos.voice.soonerBy" : "demos.voice.laterBy", { s: Math.abs(s) })}
                  onClick={() => save(nudgeVoice(track.offsets, starts, replay.tickRate, tick, s))}
                >
                  {s > 0 ? "+" : "−"}
                  {Math.abs(s)}
                </button>
              ))}
            </div>
            <div className="voice-nudge-ends muted small" aria-hidden="true">
              <span>← {t("demos.voice.sooner")}</span>
              <span>{t("demos.voice.later")} →</span>
            </div>
          </fieldset>

          <section className="voice-ear" aria-labelledby={`ear-${track.id}`}>
            <h3 id={`ear-${track.id}`} className="eyebrow">
              {t("demos.voice.byEar")}
            </h3>
            <ol className="voice-steps small">
              <li>{t("demos.voice.step1")}</li>
              <li>{t("demos.voice.step2")}</li>
              <li>{t("demos.voice.step3")}</li>
            </ol>
            <audio
              ref={preview}
              controls
              preload="metadata"
              src={voiceAudioUrl(demo.id, track.id)}
              onTimeUpdate={(e) => setHeardAt(e.currentTarget.currentTime)}
              onSeeked={(e) => setHeardAt(e.currentTarget.currentTime)}
            />
            <button type="button" className="btn btn-primary btn-small" onClick={() => save(syncVoiceAt(track.offsets, starts, replay.tickRate, tick, heardAt))}>
              <Icon name="sync" />
              {t("demos.voice.matchHere", { audio: audioClock(heardAt), at: here })}
            </button>
          </section>

          <form
            className="voice-typed"
            onSubmit={(e) => {
              e.preventDefault();
              if (typedSeconds === null) return;
              save(syncVoiceAt(track.offsets, starts, replay.tickRate, tick, typedSeconds));
              setTyped("");
            }}
          >
            <label className="field">
              <span>{t("demos.voice.typed", { at: here })}</span>
              <input value={typed} inputMode="decimal" placeholder="12:34.5" aria-invalid={typed !== "" && typedSeconds === null} onChange={(e) => setTyped(e.target.value)} />
            </label>
            <button type="submit" className="btn btn-secondary btn-small" disabled={typedSeconds === null}>
              {t("demos.voice.apply")}
            </button>
          </form>

          <details className="voice-more">
            <summary>{t("demos.voice.more")}</summary>
            <form
              className="voice-typed"
              onSubmit={(e) => {
                e.preventDefault();
                if (name.trim()) update.mutate({ id: track.id, label: name }, { onError: fail });
              }}
            >
              <label className="field">
                <span>{t("demos.voice.label")}</span>
                <input value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
              </label>
              <button type="submit" className="btn btn-secondary btn-small" disabled={!name.trim() || name.trim() === track.label}>
                {t("demos.voice.rename")}
              </button>
            </form>
            <div className="row">
              <button type="button" className="btn btn-quiet btn-small" onClick={() => save([])}>
                {t("demos.voice.reset")}
              </button>
              {confirmDelete ? (
                <button type="button" className="btn btn-danger btn-small" disabled={del.isPending} onClick={() => del.mutate(track.id, { onError: fail })}>
                  {t("demos.voice.deleteSure")}
                </button>
              ) : (
                <button type="button" className="btn btn-quiet btn-small" onClick={() => setConfirmDelete(true)}>
                  <Icon name="trash" />
                  {t("demos.voice.delete")}
                </button>
              )}
            </div>
          </details>
        </>
      )}
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
